import {
  appDisplayComponents as adc,
  applicationPortalDialog as fdy,
  applicationPortalPromotionComponents as promo,
} from "./foundry-parts.generated";
/**
 * @fileoverview AppCard — one app entry in AppPortalDialog's result list: app
 * icon + title (optionally with an inline tag like "Beta") + muted description
 * + FavoriteStar, in an `<a>`.
 *
 * THE ANCHOR IS THE ROW, and it used to be wrapped in a `div` that carried the
 * click handler and the coming-soon class. That wrapper does not exist in
 * Foundry — the list's children are the anchors themselves — so every card sat
 * one level deeper than its counterpart and the whole subtree below it compared
 * against the wrong nodes. The handler and the state now live on the anchor.
 *
 * Foundry's own row spans three modules, which is why the class list is long:
 * `__container__1bw4eg0` is the layout, `__internal-link__1qiq4rq` and
 * `__-clickable__1qiq4rq` are the link behaviour, `__is-selected__1bw4eg0` the
 * selected state.
 *
 * TWO CLASS FAMILIES ARE DELIBERATELY NOT COPIED. `ztv-walkthrough-*` is
 * Foundry's own product-tour hook — an internal tooling reference with no
 * styling attached — and `resource-icon__*` are per-app icon tiles from a sprite
 * this app does not have; ours are Blueprint glyphs.
 */

import type { IconName } from "@blueprintjs/core";
import type { CSSProperties, MouseEvent, ReactNode } from "react";
import { FavoriteStar } from "../atoms/favorite-star";

interface AppCardProps {
  /** Blueprint icon name (apps without a custom icon fall back to a generic icon). */
  icon: IconName;
  /** Per-app accent color (Foundry tints each app's icon block with its own color). */
  iconColor?: string;
  title: string;
  description: string;
  /** Navigation href — this component IS the `<a>`. */
  href: string;
  /** Highlighted when this card is the currently-selected one in the dialog. */
  selected?: boolean;
  /**
   * Not yet built. Foundry has no such state — every app it lists is real — but
   * ours genuinely has unbuilt ones, so it has to be shown somewhere.
   */
  comingSoon?: boolean;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  /** Optional inline tag (e.g. <AppDisplayTag>Beta</AppDisplayTag>). */
  titleTag?: ReactNode;
  favorited?: boolean;
  onToggleFavorite?: () => void;
  className?: string;
}

export function AppCard({
  icon,
  iconColor,
  title,
  description,
  href,
  selected = false,
  comingSoon = false,
  onClick,
  titleTag,
  favorited = false,
  onToggleFavorite,
  className,
}: AppCardProps) {
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return (
    <a data-testid={`app-card-${slug}`}
      href={href}
      tabIndex={0}
      onClick={onClick}
      className={[
        fdy["container"],
        fdy["internal-link"],
        fdy["-clickable"],
        selected ? fdy["is-selected"] : "",
        "bp-app-card",
        selected ? "is-selected" : "",
        comingSoon ? "bp-app-portal-card-disabled" : "bp-app-portal-card-live",
        className,
      ].filter(Boolean).join(" ")}
    >
      <div className={`${fdy["title-and-icon-container"]} bp-app-card-title-and-icon`}>
        {/*
          THE TILE PAINTS ITS OWN GLYPH — no child element.

          Foundry's tile is a `<span>` with a background-image sprite and nothing
          inside it. Ours held a Blueprint `<Icon>`, which renders
          `<span class="bp6-icon"><svg><path>` — three DOM nodes per row that have
          no counterpart, and with twenty rows that was the largest single source
          of structural difference in the launcher.

          Blueprint icons are also a FONT. `bp6-icon-<name>` puts the glyph in a
          `::before`, which is a pseudo-element and not a node, so the tree
          matches while the icon still renders — Blueprint's own CSS API, not a
          workaround. (The two unscoped-looking `[class*=bp6-icon-]` rules in the
          bundle are `.bp6-callout[…]` and `.bp6-button.bp6-loading[…]`; neither
          reaches this.)

          `aria-hidden` because it is decorative: the application's name is the
          next thing in the row and is real text.

          Size is INLINE, as the capture is. Foundry's own `__icon__` rule
          declares 28px and every rendered span overrides it to 30 with a style
          attribute; that rule is two classes deep, so a 30px declaration in our
          stylesheet lost to it and the tile came out 28px.
        */}
        <span
          aria-hidden
          className={`${fdy["icon"]} ${fdy["app-icon"]} bp-app-card-icon bp6-icon-${icon}`}
          style={{ height: 30, width: 30, "--app-icon-color": iconColor } as CSSProperties}
        />
        <div className={`${fdy["title-and-description"]} bp-app-card-title-and-description`}>
          <div className={`${fdy["title"]} bp-app-card-title`}>
            <div className={adc["wrapper-row"]}>{title}</div>
            {titleTag}
          </div>
          <div className={`bp6-text-muted ${fdy["description"]} bp-app-card-description`}>
            <span>{description}</span>
          </div>
        </div>
      </div>
      <div className={`${fdy["buttons"]} bp-app-card-buttons`}>
        {/*
          Foundry's star is a `bp6-button bp6-minimal` wearing classes from two
          modules: `__fav-icon__1bw4eg0` places it in the row, and the promotion
          feature owns the star itself (`favorite-icon`, plus `favorited` when
          it is on).
        */}
        <FavoriteStar
          favorited={favorited}
          onToggle={onToggleFavorite}
          ariaLabel={favorited ? "Remove from favorites" : "Add as favorite"}
          className={[
            "bp6-button",
            "bp6-minimal",
            fdy["fav-icon"],
            promo["favorite-icon"],
            favorited ? promo["favorited"] : "",
          ].filter(Boolean).join(" ")}
        />
      </div>
    </a>
  );
}
